import { create } from 'zustand'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { getVehicleDueDate } from '../pages/vehicleManagerUtils'
import type { Vehicle, Statistics, PaginatedData } from '../types'

const normalizePlate = (value?: string) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[—–－]/g, '-')

interface VehicleState {
  vehicles: Vehicle[]
  statistics: Statistics | null
  loading: boolean

  fetchVehicles: () => Promise<void>
  fetchStatistics: () => Promise<void>
  getVehiclesByPage: (status: string, page: number, pageSize: number) => Promise<PaginatedData<Vehicle>>
  searchVehicles: (keyword: string) => Promise<Vehicle[]>
  addVehicle: (vehicle: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateVehicle: (id: string, updates: Partial<Vehicle>) => Promise<void>
  deleteVehicle: (id: string) => Promise<void>
  clearCache: () => void
}

const emptyStatistics: Statistics = {
  total: 0,
  available: 0,
  maintenance: 0,
  rented: 0,
  expiring: 0,
  contracted: 0,
  expired: 0
}

export const useVehicleStore = create<VehicleState>((set, get) => ({
  vehicles: [],
  statistics: null,
  loading: false,

  fetchVehicles: async () => {
    set({ loading: true })
    try {
      const result = await simpleDirectCloudApi.getAllVehiclesWithOutstanding()
      if (result.success && result.data) {
        set({ vehicles: result.data })
      } else {
        throw new Error(result.error || '获取车辆失败')
      }
    } finally {
      set({ loading: false })
    }
  },

  fetchStatistics: async () => {
    try {
      if (get().vehicles.length === 0) {
        await get().fetchVehicles()
      }

      const vehicles = get().vehicles
      const now = new Date()
      const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

      const statusCount: Record<string, number> = {
        available: 0,
        rented: 0,
        maintenance: 0,
        contracted: 0,
        inactive: 0,
        unknown: 0
      }

      let expiringCount = 0
      let expiredCount = 0

      vehicles.forEach(vehicle => {
        if (statusCount.hasOwnProperty(vehicle.status)) {
          statusCount[vehicle.status]++
        } else {
          statusCount.unknown++
        }

        const dueDateValue = (vehicle as any)._dueDate || getVehicleDueDate(vehicle)
        const dueDate = new Date(dueDateValue)
        if (!isNaN(dueDate.getTime())) {
          if (dueDate < now) {
            expiredCount++
          } else if (dueDate <= sevenDaysLater) {
            expiringCount++
          }
        }
      })

      const statistics: Statistics = {
        total: vehicles.length,
        available: statusCount.available,
        maintenance: statusCount.maintenance,
        rented: statusCount.rented,
        expiring: expiringCount,
        contracted: statusCount.contracted,
        expired: expiredCount
      }

      set({ statistics })
    } catch (error) {
      set({ statistics: emptyStatistics })
      throw error
    }
  },

  getVehiclesByPage: async (status: string, page: number, pageSize: number) => {
    const result = await simpleDirectCloudApi.getVehiclesByPage(status, page, pageSize)
    if (result.success && result.data) {
      return {
        data: result.data,
        hasMore: Boolean(result.hasMore),
        total: result.total ?? result.data.length
      }
    }
    throw new Error(result.error || '分页获取车辆失败')
  },

  searchVehicles: async (keyword: string) => {
    if (!keyword.trim()) return []
    const result = await simpleDirectCloudApi.searchVehicles(keyword)
    if (result.success && result.data) {
      set({ vehicles: result.data })
      return result.data
    }
    throw new Error(result.error || '搜索失败')
  },

  addVehicle: async vehicleData => {
    const vehicles = get().vehicles
    const normalizedPlate = normalizePlate(vehicleData.plate)
    if (vehicles.some(v => normalizePlate(v.plate) === normalizedPlate)) {
      throw new Error('这辆车已存在，不能重复新增')
    }

    const result = await simpleDirectCloudApi.addVehicle({
      ...vehicleData,
      plate: normalizedPlate
    })
    if (result.success && result.data) {
      set({ vehicles: [...vehicles, result.data] })
    } else {
      throw new Error(result.error || '新增失败')
    }
  },

  updateVehicle: async (id: string, updates) => {
    const docId = (updates as any)._id || id
    const result = await simpleDirectCloudApi.updateVehicle(docId, updates)
    if (result.success) {
      const vehicles = get().vehicles
      const index = vehicles.findIndex(v => (v._id || v.id) === docId)
      if (index !== -1) {
        const updatedVehicle = {
          ...vehicles[index],
          ...updates,
          updatedAt: new Date().toISOString()
        }
        const newVehicles = [...vehicles]
        newVehicles[index] = updatedVehicle
        set({ vehicles: newVehicles })
      }
    } else {
      throw new Error(result.error || '更新失败')
    }
  },

  deleteVehicle: async (id: string) => {
    const result = await simpleDirectCloudApi.deleteVehicle(id)
    if (result.success) {
      const vehicles = get().vehicles
      const filtered = vehicles.filter(v => (v._id || v.id) !== id)
      set({ vehicles: filtered })
    } else {
      throw new Error(result.error || '删除失败')
    }
  },

  clearCache: () => {
    set({ vehicles: [], statistics: null })
  }
}))
